import { useState } from 'react';
import { useEffect } from 'react';
import useZipPhotos from './hooks/useZipPhotos';
import moment from 'moment-timezone';
import CopyToClipboard from './CopyToClipboard';

export default function DownloadRequestList({
    activationId,
}: {
    activationId: number;
}) {
    const { getZipRequestsForActivation } = useZipPhotos();
    const [zipRequests, setZipRequests] = useState<any[]>([]);

    useEffect(() => {
        const load = async () => {
            const zipRequests = await getZipRequestsForActivation(activationId);
            setZipRequests(zipRequests.data);
        };

        load();

        const interval = setInterval(() => {
            load();
        }, 10000);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    if (zipRequests.length === 0) {
        return null;
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8 pb-10">
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-base font-semibold text-gray-900">
                        Download Requests
                    </h1>
                    <p className="mt-2 text-sm text-gray-700">
                        A list of all the download requests for this activation.
                    </p>
                </div>
                <div className="grow" />
                <div className="inline-title"></div>
            </div>
            <div className="mt-8 flow-root">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                        <table className="min-w-full divide-y divide-gray-300">
                            <thead>
                                <tr>
                                    <th
                                        scope="col"
                                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3 border-gray-200 bg-gray-50"
                                    >
                                        Destination
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 border-gray-200 bg-gray-50"
                                    >
                                        Status
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 border-gray-200 bg-gray-50"
                                    >
                                        Created At
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 border-gray-200 bg-gray-50"
                                    >
                                        Completed At
                                    </th>

                                    <th
                                        scope="col"
                                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 border-gray-200 bg-gray-50"
                                    >
                                        Zip Link
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white">
                                {zipRequests.map((zipRequest) => (
                                    <tr
                                        key={zipRequest.id}
                                        className="even:bg-gray-50"
                                    >
                                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                                            {zipRequest.zipRequestDestination}
                                        </td>
                                        <td className="whitespace-nowrap capitalize px-3 py-4 text-sm text-gray-500 flex items-center">
                                            {zipRequest.status}
                                            {zipRequest.status ===
                                                'pending' && (
                                                <div
                                                    role="status"
                                                    className="ml-2"
                                                >
                                                    <svg
                                                        aria-hidden="true"
                                                        className="inline w-4 h-4 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                                                        viewBox="0 0 100 101"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                                            fill="currentColor"
                                                        />
                                                        <path
                                                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                                            fill="currentFill"
                                                        />
                                                    </svg>
                                                    <span className="sr-only">
                                                        Loading...
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                            {moment(
                                                zipRequest.createdAt,
                                            ).format('MM/DD/YYYY hh:mm:ss a')}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                            {zipRequest.completedAt
                                                ? moment(
                                                      zipRequest.completedAt,
                                                  ).format(
                                                      'MM/DD/YYYY hh:mm:ss a',
                                                  )
                                                : ' - '}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 flex items-center">
                                            {zipRequest.zipRequestUri ? (
                                                <>
                                                    <a
                                                        href={
                                                            zipRequest.zipRequestUri
                                                        }
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Download
                                                    </a>
                                                    <CopyToClipboard
                                                        text={
                                                            zipRequest.zipRequestUri
                                                        }
                                                    />
                                                </>
                                            ) : (
                                                ' - '
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
